import { NavLink, useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { LinkIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import { Crown } from 'iconoir-react'
import { snakeCase } from 'lodash'
import { ReactElement, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { useConfirmation } from '../ConfirmationContext'
import { LockIcon } from '../contacts/LockIcon'
import ChipV2 from './ChipV2'
import { isSystemPipelineId } from 'app/components/action-center/systemPipelines'
import { Tooltip } from 'app/components/Tooltip'
import { useOpenCustomPageEditor } from 'app/hooks/useOpenCustomPageEditor'
import type { CustomPageJson } from 'app/types/entities'
import type { PipelineType } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser } from 'app/utils'
import { titleCase } from 'app/utils/titleCase'
import { debug } from 'lib/logging'
import { isManualListPipeline } from 'lib/lists'
import { roundCount } from 'lib/formatCount'
import { NavItemType } from 'types/Sidebar'
import { usePipelineStore } from 'app/stores/StoreContext'

// observer: reads a feature toggle (and possibly authStore.user) in render; useFeatureToggle resolves async and its contract requires an observer wrapper (TRK-6020).
export const NavItem = observer(function NavItem({
  item,
  isDefault,
  lockedDefault,
  customPage,
  pipeline,
  onEditList,
  onSetAsDefault,
  onRemovePipeline,
}: {
  item: NavItemType
  isDefault?: boolean
  lockedDefault?: boolean
  customPage?: CustomPageJson
  pipeline?: PipelineType
  onEditList?: () => void
  onRemovePipeline?: () => void
  onSetAsDefault?: (value: string | undefined) => Promise<void>
}): ReactElement {
  const user = useUser()
  const [confirm] = useConfirmation()
  if (!user) return <></>

  const navigate = useNavigate()
  const [tooltipMessage, setTooltipMessage] = useState<string | undefined>(undefined)
  const pipelineStore = usePipelineStore()
  const openCustomPageEditor = useOpenCustomPageEditor()
  // state to avoid refreshing the page when a custom page is deleted
  const [isDeleted, setIsDeleted] = useState<boolean>(false)

  if (isDeleted) return <></>

  /** When locked, render a div instead of NavLink to prevent browser URL preview.
   * The Tooltip component sets pointerEvents: 'auto' on its child (line 113 in Tooltip.tsx),
   * which overrides the parent NavLink's pointer-events-none. This causes the browser to still
   * detect the link and show the URL preview over the text area and navigation to the locked page. By rendering a plain div
   * instead of NavLink, there's no link element at all, so no navigation to the locked page.
   */
  if (item.isLocked) {
    return (
      <div
        x-nav-selector={snakeCase(item.name)}
        x-component-name="locked-nav-item"
        aria-label={item.name}
        className="flex h-9 items-center space-x-2 "
        tabIndex={-1}
      >
        <div className="flex h-full w-9 shrink-0 items-center justify-center pl-3">
          <hr className="h-full w-0.5 border-y-0 border-l-2 border-r-0 border-default" />
        </div>
        <div className="flex items-center gap-x-2 px-2 text-default opacity-50">
          <span className="pointer-events-none min-w-0 truncate text-label-sm">{titleCase(item.name)}</span>
          <LockIcon message="This content is unavailable because you don't have any locations with an active PMS integration" />
        </div>
      </div>
    )
  }

  return (
    <NavLink
      to={item.href ?? `#`}
      x-nav-selector={snakeCase(item.name)}
      x-component-name="nav-item"
      aria-label={item.name}
      className="group/nav-item flex h-9 items-center space-x-2"
    >
      <div className="flex h-full w-9 shrink-0 items-center justify-center pl-2.75">
        <hr
          className={twMerge(
            `h-full w-0.5 border-y-0 border-l-2 border-r-0`,
            item.active ? `border-(--fg-strong)` : `border-default`,
          )}
        />
      </div>
      <div className="flex h-full w-full grow cursor-pointer items-center overflow-hidden py-px">
        <div
          className={classNames(
            `flex h-full grow items-center justify-between overflow-hidden text-left text-body-sm`,
            twMerge(
              `px-2 text-default`,
              item.active ? `rounded-md bg-(--fg-strong)/6` : `hover:rounded-md hover:bg-(--fg-strong)/4`,
            ),
          )}
        >
          <div className="flex w-full min-w-0 grow items-center gap-x-2">
            {item.linkedToActionCenter && (
              <LinkIcon
                size={12}
                weight="bold"
                role="img"
                alt="Linked to the Action Center"
                className="shrink-0 text-minimal"
              />
            )}
            <Tooltip message={tooltipMessage} className="flex min-w-0 shrink cursor-pointer!">
              <span
                className={classNames(`min-w-0 truncate text-label-sm`, item.active && `font-semibold`)}
                onMouseOver={(event) => {
                  if (
                    event.currentTarget instanceof HTMLElement &&
                    event.currentTarget.scrollWidth > event.currentTarget.clientWidth
                  ) {
                    setTooltipMessage(titleCase(item.name))
                  } else setTooltipMessage(undefined)
                }}
              >
                {titleCase(item.name)}
              </span>
            </Tooltip>
            {item.superUserOnly && (
              <div className={`rounded-lg bg-amber-500/8 p-1 ${item.active ? `bg-amber-500` : `bg-amber-500/8`}`}>
                <Crown width={14} height={14} className="text-amber-800" />
              </div>
            )}
          </div>
          <ChipV2
            href={item.href}
            total={roundCount(item.count ?? 0)}
            hide={item.count == null}
            customPage={customPage}
            pipeline={pipeline}
            isDefault={isDefault}
            lockedDefault={lockedDefault}
            isActive={item.active}
            countColor={item.countColor}
            onSetAsDefault={async () => {
              if (isDefault) {
                await onSetAsDefault?.(undefined)
                notifySuccess(`Homepage reset`)
              } else {
                await onSetAsDefault?.(item.href)
                notifySuccess(`Homepage set to ${titleCase(item.name)}`)
              }
            }}
            onRemovePipeline={
              // Never wired up for a system pipeline: it is a frontend constant, not a
              // record, and deletePipeline 404s ("Not found") on its reserved id. Omitting
              // the handler (rather than letting it fail inside the confirm dialog) hides
              // the menu entry entirely, see ChipV2's canRemovePipeline.
              isSystemPipelineId(pipeline?.id)
                ? undefined
                : async () => {
                    if (pipeline && item.href) {
                      await confirm({
                        title: `Delete pipeline`,
                        description: `Are you sure you want to delete this pipeline?`,
                        variant: `danger`,
                      }).then(async () => {
                        const result = await pipelineStore.remove(pipeline.id!)
                        if (result.error) {
                          notifyError(
                            `There was an error deleting your pipeline: ${titleCase(pipeline.name)}`,
                            `pipeline-delete-error`,
                          )
                          debug(`pipelines`, `error deleting pipeline`, result.error.message)
                        } else {
                          notifySuccess(`${titleCase(pipeline.name)} page deleted`, `pipeline-delete`)
                          onRemovePipeline?.()
                          const [next] = pipelineStore.hits.filter((candidate) => isManualListPipeline(candidate))
                          if (next) {
                            navigate(`/pipelines?tab=${next.id}`, { replace: true })
                          } else {
                            navigate(`/pipelines`, { replace: true })
                          }
                        }
                      })
                    }
                  }
            }
            onEditList={
              // A pipeline nav item never carries a customPage, so this never fires for a
              // pipeline today; gated the same way as onRemovePipeline in case that changes.
              isSystemPipelineId(pipeline?.id)
                ? undefined
                : () => {
                    const pageType = item.href?.includes(`/contacts`)
                      ? `contacts`
                      : item.href?.includes(`/tasks`)
                        ? `tasks`
                        : item.href?.includes(`/activities`)
                          ? `activities`
                          : undefined
                    if (customPage && pageType && onEditList) {
                      openCustomPageEditor({
                        customPage,
                        pageType,
                        onSaved: onEditList,
                        onDeleted: () => setIsDeleted(true),
                      })
                    }
                  }
            }
          />
        </div>
      </div>
    </NavLink>
  )
})
